import React from "react";
import clsx from "clsx";
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";

type PaginationBarProps = {
  ariaLabel?: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  disabled?: boolean;
  siblingCount?: number;
  className?: string;
  variant?: "card" | "embedded";
};

const clampPage = (page: number, totalPages: number) =>
  Math.min(Math.max(1, page), Math.max(1, totalPages));

const buildPageWindow = (page: number, totalPages: number, siblingCount: number) => {
  if (totalPages <= 7 + siblingCount * 2) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const leftBoundary = Math.max(2, page - siblingCount);
  const rightBoundary = Math.min(totalPages - 1, page + siblingCount);
  const pages: Array<number | "ellipsis"> = [1];

  if (leftBoundary > 2) {
    pages.push("ellipsis");
  }

  for (let current = leftBoundary; current <= rightBoundary; current += 1) {
    pages.push(current);
  }

  if (rightBoundary < totalPages - 1) {
    pages.push("ellipsis");
  }

  pages.push(totalPages);
  return pages;
};

const PaginationBar = ({
  ariaLabel = "Pagination",
  page,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  disabled = false,
  siblingCount = 1,
  className,
  variant = "card",
}: PaginationBarProps) => {
  const safeTotalPages = Math.max(1, totalPages);
  const currentPage = clampPage(page, safeTotalPages);
  const canGoBack = !disabled && currentPage > 1;
  const canGoForward = !disabled && currentPage < safeTotalPages;
  const pageWindow = buildPageWindow(currentPage, safeTotalPages, Math.max(0, siblingCount));
  const hasItems = totalItems !== undefined && totalItems > 0;
  const startItem =
    hasItems && pageSize !== undefined ? (currentPage - 1) * pageSize + 1 : undefined;
  const endItem =
    hasItems && pageSize !== undefined
      ? Math.min(currentPage * pageSize, totalItems ?? currentPage * pageSize)
      : undefined;
  const buttonClassName =
    "flex h-9 w-9 items-center justify-center rounded-lg border border-outline bg-surface text-text-mid transition hover:border-outline-strong hover:bg-muted-surface hover:text-text disabled:cursor-not-allowed disabled:border-transparent disabled:bg-transparent disabled:text-text-dim disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/25";
  const pageButtonClassName =
    "flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm font-medium tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/25";

  if (variant === "embedded") {
    return (
      <nav
        aria-label={ariaLabel}
        className={clsx("flex items-center justify-end gap-3 py-2", className)}
      >
        <span aria-live="polite" className="text-xs tabular-nums text-text-dim">
          {startItem !== undefined && endItem !== undefined
            ? `${startItem}–${endItem} of ${totalItems}`
            : `Page ${currentPage} of ${safeTotalPages}`}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Previous page"
            title="Previous page"
            disabled={!canGoBack}
            onClick={() => onPageChange(currentPage - 1)}
            className={buttonClassName}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next page"
            title="Next page"
            disabled={!canGoForward}
            onClick={() => onPageChange(currentPage + 1)}
            className={buttonClassName}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={clsx(
        "flex flex-col gap-3 rounded-xl border border-outline bg-surface px-4 py-3 shadow-sm",
        "sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-text">
          Page {currentPage} of {safeTotalPages}
        </p>
        {hasItems && (
          <p className="text-xs text-text-dim">
            {startItem !== undefined && endItem !== undefined
              ? `Showing ${startItem}-${endItem} of ${totalItems}`
              : `${totalItems} results`}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          aria-label="First page"
          title="First page"
          disabled={!canGoBack}
          onClick={() => onPageChange(1)}
          className={buttonClassName}
        >
          <ChevronFirst className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Previous page"
          title="Previous page"
          disabled={!canGoBack}
          onClick={() => onPageChange(currentPage - 1)}
          className={buttonClassName}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        <div className="flex flex-wrap items-center gap-1.5">
          {pageWindow.map((entry, idx) =>
            entry === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-sm text-text-dim">
                ...
              </span>
            ) : (
              <button
                key={entry}
                type="button"
                aria-current={entry === currentPage ? "page" : undefined}
                disabled={disabled || entry === currentPage}
                onClick={() => onPageChange(entry)}
                className={clsx(
                  pageButtonClassName,
                  entry === currentPage
                    ? "border-outline-strong bg-muted-surface text-text shadow-sm"
                    : "border-outline bg-surface text-text-mid hover:border-outline-strong hover:bg-muted-surface hover:text-text",
                )}
              >
                {entry}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          aria-label="Next page"
          title="Next page"
          disabled={!canGoForward}
          onClick={() => onPageChange(currentPage + 1)}
          className={buttonClassName}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Last page"
          title="Last page"
          disabled={!canGoForward}
          onClick={() => onPageChange(safeTotalPages)}
          className={buttonClassName}
        >
          <ChevronLast className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
};

export type { PaginationBarProps };
export default PaginationBar;
